import { ApiError, apiFetch } from './http';
import { buildEnrollmentProductApiPath, buildSiteApiPath } from './routes';

export class SubmissionError extends ApiError {
  fieldErrors?: Record<string, string>;
  correlationId?: string;
  messages?: string[];
  resultId?: string;

  constructor(message: string, status: number, options?: { fieldErrors?: Record<string, string>; correlationId?: string; messages?: string[]; resultId?: string }) {
    super(message, status);
    this.name = 'SubmissionError';
    this.fieldErrors = options?.fieldErrors;
    this.correlationId = options?.correlationId;
    this.messages = options?.messages;
    this.resultId = options?.resultId;
  }
}

export interface SubmitFormResponse {
  ok: true;
  result_id: string;
  correlation_id?: string;
}

export interface ValidateEligibilityResponse {
  eligible: true;
  correlationId?: string;
  resultId?: undefined;
}

export interface IneligibleEligibilityResponse {
  eligible: false;
  correlationId?: string;
  resultId: string;
}

function terminalResultError(message: string, status: number, body: { correlation_id?: string; result_id?: string; fieldErrors?: Record<string, string>; messages?: string[] }): SubmissionError {
  if (!body.result_id) {
    return new SubmissionError(message, status, {
      correlationId: body.correlation_id,
      fieldErrors: body.fieldErrors,
      messages: body.messages,
    });
  }

  return new SubmissionError(message, status, {
    correlationId: body.correlation_id,
    fieldErrors: body.fieldErrors,
    messages: body.messages,
    resultId: body.result_id,
  });
}

export function validateEligibility(siteType: string, values: Record<string, unknown>): Promise<ValidateEligibilityResponse | IneligibleEligibilityResponse> {
  return validateEligibilityAtPath(buildSiteApiPath(siteType, '/validate-eligibility'), values);
}

export function validateEnrollmentProductEligibility(product: string, values: Record<string, unknown>): Promise<ValidateEligibilityResponse | IneligibleEligibilityResponse> {
  return validateEligibilityAtPath(buildEnrollmentProductApiPath(product, '/validate-eligibility'), values);
}

async function validateEligibilityAtPath(path: string, values: Record<string, unknown>): Promise<ValidateEligibilityResponse | IneligibleEligibilityResponse> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values }),
  });

  const body = await res.json().catch(() => ({})) as {
    eligible?: boolean;
    failedQuestionIds?: string[];
    error?: string;
    messages?: string[];
    correlation_id?: string;
    result_id?: string;
  };

  if (!res.ok) {
    if (res.status === 401) {
      throw new SubmissionError(body.error ?? `Eligibility check failed: ${res.status}`, res.status, {
        messages: body.messages,
        correlationId: body.correlation_id,
        resultId: body.result_id,
      });
    }

    throw terminalResultError(body.error ?? `Eligibility check failed: ${res.status}`, res.status, body);
  }

  const eligible = typeof body.eligible === 'boolean'
    ? body.eligible
    : (body.failedQuestionIds ?? []).length === 0;

  if (!eligible) {
    if (!body.result_id) {
      throw new SubmissionError('Eligibility validation returned an ineligible result without a result ID.', res.status, {
        correlationId: body.correlation_id,
      });
    }

    return {
      eligible: false,
      correlationId: body.correlation_id,
      resultId: body.result_id,
    };
  }

  return {
    eligible: true,
    correlationId: body.correlation_id,
  };
}

export function submitForm(siteType: string, values: Record<string, unknown>, draftId?: string): Promise<SubmitFormResponse> {
  return submitFormAtPath(buildSiteApiPath(siteType, '/submit'), values, draftId);
}

export function submitEnrollmentProductForm(product: string, values: Record<string, unknown>, draftId?: string): Promise<SubmitFormResponse> {
  return submitFormAtPath(buildEnrollmentProductApiPath(product, '/submit'), values, draftId);
}

async function submitFormAtPath(path: string, values: Record<string, unknown>, draftId?: string): Promise<SubmitFormResponse> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values, draftId }),
  });

  const body = await res.json().catch(() => ({})) as {
    error?: string;
    fieldErrors?: Record<string, string>;
    messages?: string[];
    correlation_id?: string;
    result_id?: string;
    ok?: true;
  };

  if (!res.ok) {
    if (res.status === 401) {
      throw new SubmissionError(body.error ?? `Failed to submit enrollment: ${res.status}`, res.status, {
        fieldErrors: body.fieldErrors,
        messages: body.messages,
        correlationId: body.correlation_id,
        resultId: body.result_id,
      });
    }

    throw terminalResultError(body.error ?? `Failed to submit enrollment: ${res.status}`, res.status, body);
  }

  if (!body.result_id) {
    throw new SubmissionError('Submission completed without a result ID.', res.status, {
      correlationId: body.correlation_id,
    });
  }

  return {
    ok: true,
    result_id: body.result_id,
    correlation_id: body.correlation_id,
  };
}